import React from "react";
import IssuanceLayout from "layouts/IssuanceLayout";
import {
  Button,
  Stack,
  MenuList,
  MenuItem,
  ListItemText,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
} from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import ParallelSection from "components/issuance/parallelSections";
import Spreadsheet from "components/Spreadsheet";
import MySwal from "utils/Swal";
import validator from "validator";
import useMediaQuery from "@mui/material/useMediaQuery";
import CustomPopover from "components/common/customPopover";
import Dropzone from "react-dropzone";
import CustomDialog from "components/common/CustomDialog";
import RecipientsTable from "components/RecipientsTable";
import { useAuth } from "hooks/useAuth";
import Papa from "papaparse";
// import _ from "lodash";
import { useIssuanceDataProvider } from "hooks/useIssuanceDataProvider";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const initialRows = [{}, {}, {}, {}, {}, {}, {}, {}, {}];

export default function Recipients() {
  const { issuanceData, setIssuanceData } = useIssuanceDataProvider();
  const { issuanceType } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [csvFile, setCsvFile] = React.useState();
  const [shouldShowValidation, setShouldShowValidation] = React.useState(false);
  const [formData, setFormData] = React.useState({
    templateId: issuanceData?.templateId,
    csvData: issuanceData?.recipients?.csvData.length
      ? issuanceData?.recipients?.csvData
      : initialRows,
    mappedFileNameColumn: "Recipient Name",
    metaData: [],
    imageData: {},
    tags: issuanceData?.tags,
    skills: issuanceData?.skills,
    isFromFE: true,
    sharedWithOrganization: issuanceData?.sharedWithOrganization,
    organizationEmail: issuanceData?.organizationEmail,
  });
  const [drawer, setDrawer] = React.useState(false);

  const notificationPlatform =
    issuanceData?.credentialDetails?.notificationPlatform;

  const columns = [
    ...new Set([
      ...(issuanceType === "existing" ? ["File Name"] : []),
      "Recipient Name",
      ...(notificationPlatform?.email && !formData?.sharedWithOrganization ? ["Email Address"] : []),
      ...(notificationPlatform?.whatsapp && !formData?.sharedWithOrganization ? ["WhatsApp Number"] : []),
      ...(issuanceData?.template?.placeholders?.textPlaceholders?.filter(
        (p) => p !== "Reference Number",
      ) ?? []),
    ]),
  ];

  const validateData = (formData) => {
    const csvData = [];
    formData?.csvData?.forEach((row) => {
      let isEmptyRow = true;
      for (let key in row) {
        if (row[key]) {
          isEmptyRow = false;
          break;
        }
      }
      if (!isEmptyRow) {
        csvData.push({ ...row });
      }
    });

    if (csvData.length === 0) {
      return {
        title: "Please fill data",
        text: "No data has been inserted into the sheet",
      };
    }

    const recipientsNamesMissing = !csvData.every(
      (obj) => obj?.["Recipient Name"],
    );
    const emailAddressesMissing =
      (notificationPlatform?.email && !formData?.sharedWithOrganization) &&
      !csvData.every((obj) => validator.isEmail(obj["Email Address"] ?? ""));

    const whatsappNumMissing =
      (notificationPlatform?.whatsapp && !formData?.sharedWithOrganization) &&
      !csvData.every((obj) =>
        validator.isMobilePhone(obj["WhatsApp Number"] ?? ""),
      );
    const organizationEmailMissingOrInvalid =
      (formData?.sharedWithOrganization && !validator.isEmail(formData?.organizationEmail ?? ""));

    if (recipientsNamesMissing || emailAddressesMissing || whatsappNumMissing || organizationEmailMissingOrInvalid) {
      return {
        title: "Incomplete Information",
        text: recipientsNamesMissing
          ? "One or more recipients are missing or incorrect."
          : emailAddressesMissing
          ? "One or more email addresses are missing or incorrect."
          : whatsappNumMissing
          ? "One or more whatsapp numbers are missing or incorrect."
          : "Organization email is missing or incorrect.",
      };
    }

    return null;
  };

  React.useEffect(() => {
    setShouldShowValidation(false);
  }, [formData]);

  const handleNext = async () => {
    setShouldShowValidation(true);
    const reqData = { ...formData };
    reqData.csvData = reqData.csvData.filter(
      (obj) => Object.keys(obj).length > 0,
    );

    if (user.plan !== "FREE" && reqData.csvData.length > user.credits) {
      MySwal.fire({
        title: "Not enough credits",
        icon: "error",
      });
      return;
    }

    const validationError = validateData(reqData);

    if (validationError) {
      MySwal.fire({
        title: validationError.title,
        text: validationError.text,
        icon: "error",
      });
    } else {
      setIssuanceData(
        {
          recipients: {
            csvData: formData.csvData,
            isCompleted: true,
          },
          sharedWithOrganization: formData?.sharedWithOrganization,
          organizationEmail: formData?.organizationEmail,
        },
        () =>
          navigate(
            `${process.env.REACT_APP_BASE_ROUTE}/issuance/${issuanceType}/send/${location.search}`,
          ),
      );
    }
  };

  const downloadSampleCsv = () => {
    let csvContent = "";
    let titles = columns.filter(
      (title) => title.toLowerCase() !== "eligibility",
    );
    csvContent += titles.map((title) => `"${title}"`).join(",") + "\n";
    const emptyData = titles.map(() => "");
    csvContent += emptyData.join(",") + "\n";
    const bom = "\uFEFF"; // UTF-8 BOM
    const encodedCSV = bom + encodeURIComponent(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", `data:text/csv;charset=utf-8,${encodedCSV}`);
    link.setAttribute("download", "sampleCSV.csv");
    document.body.appendChild(link);
    link.click();
  };

  const cellValidationFormula = ({ rowData, columnId }) => {
    let isRequiredColumn = false;
    if (
      (notificationPlatform?.email && columnId === "Email Address") ||
      (notificationPlatform?.whatsapp && columnId === "WhatsApp Number")
    ) {
      isRequiredColumn = true;
    }
    const hasData = Object.keys(rowData).length > 0;
    const isInvalidValue = !rowData?.[columnId];
    let isInvalid = false;
    if (notificationPlatform?.email && columnId === "Email Address") {
      isInvalid = !validator.isEmail(rowData[columnId] ?? "");
    } else if (
      notificationPlatform?.whatsapp &&
      columnId === "WhatsApp Number"
    ) {
      isInvalid = !validator.isMobilePhone(rowData[columnId] ?? "");
    }
    const isInvalidRecipientName = columnId === "Recipient Name";

    return (
      shouldShowValidation &&
      hasData &&
      (isInvalidValue || (isRequiredColumn && isInvalid)) &&
      (isInvalidRecipientName || isRequiredColumn)
    );
  };

  const handleCSVUpload = (files) => {
    Papa.parse(files[0], {
      complete: (result) => {
        // Check if any column title contains a dot
        const fieldWithDot = result.meta.fields.filter(field => field.includes('.'));
        if (fieldWithDot.length > 0) {
          MySwal.fire({
            title: "Invalid CSV file",
            text: "Column title cannot contain a dot (.) " + fieldWithDot.join(", "),
            icon: "error",
          });
          return;
        }
        setCsvFile(files[0]);
      },
      error: (err) => {
        MySwal.fire({
          title: "Invalid CSV file",
          text: err.message,
          icon: "error",
        });
      },
      header: true, // This assumes the first row contains the column names
    });
  };

  // Save on unmount
  const oldPathName = location.pathname;
  React.useEffect(() => {
    return () => {
      if (oldPathName !== window.location.pathname) {
        setIssuanceData({
          recipients: {
            csvData: formData.csvData,
          },
          sharedWithOrganization: formData?.sharedWithOrganization,
          organizationEmail: formData?.organizationEmail,
        });
      }
    };
  }, [formData]);

  const handleSave = (e) => {
    e.target.innerText = "Saving...";
    const callback = () => {
      e.target.innerText = "Saved!";
      setTimeout(() => {
        e.target.innerText = "Save";
      }, 5000);
    };
    setIssuanceData(
      {
        recipients: {
          csvData: formData.csvData,
        },
      },
      callback,
    );
  };

  return (
    <IssuanceLayout>
      <ParallelSection
        data={{
          recipients: {
            csvData: formData.csvData,
          },
        }}
        drawer={drawer}
        setDrawer={setDrawer}
      >
        <>
          <Stack spacing={2} data-tut="AddRecipients">
            {issuanceType === "create" && (
        <Stack direction="row" spacing={2} justifyContent="space-between">
              <Stack spacing={2} direction="row">
                <CustomDialog
                  AnchorComponent={({ onClick }) => (
                    <Button onClick={onClick} variant="outlined">
                      Recipients
                    </Button>
                  )}
                  title="Recipient History"
                  Content={(dialogProps) => {
                    const [isSeeAll, setIsSeeAll] = React.useState("false");
                    return (
                      <RecipientsTable
                        {...dialogProps}
                        isSeeAll={isSeeAll}
                        setIsSeeAll={setIsSeeAll}
                        onImport={setFormData}
                        callback={() => dialogProps?.handleClose()}
                        tableStyle={{ height: "calc(100dvh - 200px)" }}
                      />
                    );
                  }}
                />
                <CustomPopover
                  AnchorComponent={(anchorProps) => (
                    <Button
                      {...anchorProps}
                      variant="outlined"
                      endIcon={<KeyboardArrowDownOutlinedIcon />}
                    >
                      Import CSV
                    </Button>
                  )}
                  PopoverComponent={(compProps) => (
                    <MenuList {...compProps}>
                      <Dropzone
                        onDrop={(files) => {
                          handleCSVUpload(files);
                          compProps.handleClose();
                        }}
                        accept={{ "text/plain": [".csv"] }}
                      >
                        {({ getRootProps, getInputProps }) => (
                          <MenuItem {...getRootProps()}>
                            <input {...getInputProps()} />
                            <ListItemText disableTypography>
                              <Typography variant="body2">
                                Import CSV
                              </Typography>
                            </ListItemText>
                          </MenuItem>
                        )}
                      </Dropzone>
                      <MenuItem onClick={downloadSampleCsv}>
                        <ListItemText disableTypography>
                          <Typography variant="body2">
                            Download Sample CSV
                          </Typography>
                        </ListItemText>
                      </MenuItem>
                    </MenuList>
                  )}
                />
              </Stack>
                {!notificationPlatform?.whatsapp &&<FormControlLabel
                  label="Sending to Organization"
                  sx={{color:"primary.main"}}
                  control={
                    <Checkbox
                      checked={formData?.sharedWithOrganization}
                      onChange={(e) =>
                        setFormData((prevstate) => ({
                          ...prevstate,
                          sharedWithOrganization: e.target.checked,
                        }))
                      }
                    />
                  }
                />}
            </Stack>
            )}
            {issuanceType === "existing" && (
              <Stack direction="row" spacing={2} justifyContent="space-between">
              <Dropzone
                    onDrop={(files) => {
                      handleCSVUpload(files);
                    }}
                    accept={{ "text/plain": [".csv"] }}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <Button {...getRootProps()} variant="outlined">
                        <input {...getInputProps()} />
                        Import CSV
                      </Button>
                    )}
                  </Dropzone>
          </Stack>
            )}
            {formData?.sharedWithOrganization && (
              <TextField
                size="small"
                label="Organization Email"
                fullWidth
                required
                value={formData?.organizationEmail}
                autoFocus
                error={!validator.isEmail(formData?.organizationEmail ?? "")}
                InputLabelProps={{ shrink: Boolean(formData?.organizationEmail) }}
                onChange={(e) =>
                  setFormData((prevState) => ({
                    ...prevState,
                    organizationEmail: e.target.value,
                  }))
                }
              />
            )}
            <Spreadsheet
              csvFile={csvFile}
              data={formData.csvData}
              notificationPlatform={notificationPlatform}
              setData={(csvData) =>
                setFormData((prevState) => ({ ...prevState, csvData }))
              }
              columns={columns}
              cellValidationFormula={cellValidationFormula}
            />

            <Stack direction="row" spacing={2} justifyContent="center">
              {isMobile && (
                <Button
                  variant="outlined"
                  disableElevation
                  sx={{ minWidth: 150 }}
                  onClick={() => setDrawer(true)}
                >
                  Preview
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={handleSave}
                sx={{ minWidth: 100 }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                disableElevation
                sx={{ minWidth: 150 }}
                onClick={handleNext}
                data-tut="AddRecipientsNextBtn"
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </>
      </ParallelSection>
    </IssuanceLayout>
  );
}
